import Container from "@/components/Container";
import { Button } from "@/components/UI/Buttons/Button";
import { FC, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ROUTES from "@/data/Routes";
import { AdvancedMarker, Map } from "@vis.gl/react-google-maps";
import ContactModal from "@/components/ContactModal";
import phone from "@/assets/icons/phone.svg";
import calendar from "@/assets/icons/calendar.svg";
import connection from "@/assets/icons/connection.svg";

interface ContactCardProps {
  icon: string;
  title: string;
  description: React.ReactNode;
  buttonLabel: string;
  onClick?: () => void;
}

const ContactCard: FC<ContactCardProps> = ({
  icon,
  title,
  description,
  buttonLabel,
  onClick,
}) => (
  <div className="bg-surface rounded-card flex flex-1 flex-col gap-5 p-5">
    <div className="text-dark flex h-10 w-10 items-center justify-center rounded-[10px] border border-[#E7E7E7]">
      <img src={icon} alt={title} className="h-6 w-6" />
    </div>
    <div className="flex flex-1 flex-col justify-between gap-5">
      <div className="flex flex-col gap-2">
        <p className="text-dark text-base font-normal">{title}</p>
        <div className="text-muted text-base font-normal">{description}</div>
      </div>
      <Button
        variant="white"
        size="sm"
        hoverDark
        onHover
        className="text-dark !border-stroke !rounded-button !h-[52px] w-fit !px-[26px] !text-base !font-normal !shadow-none"
        onClick={onClick}
      >
        {buttonLabel}
      </Button>
    </div>
  </div>
);

const Contacts: FC = () => {
  const { hash } = useLocation();
  const navigate = useNavigate();
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.replace("#", ""));
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [hash]);

  const scrollToForm = () => {
    navigate(ROUTES.CONSULTATION);
  };

  return (
    <div className="flex w-full flex-col bg-white">
      <section className="px-container w-full pt-25 pb-10 md:pt-50">
        <Container>
          <div className="flex w-full flex-col items-center gap-5 text-center">
            <h1 className="text-6 text-dark md:text-8.5 text-center leading-[120%] font-light">
              Susisiekite su mumis
            </h1>
            <div className="text-muted max-w-[580px] text-base leading-relaxed">
              <p className="m-0 p-0">
                Reikia pagalbos dėl Telomera, narystės ar partnerystės? Mūsų
                komanda pasiruošusi padėti.
              </p>
            </div>
          </div>
        </Container>
      </section>

      <section className="px-container w-full pb-5">
        <Container>
          <div className="flex w-full flex-col gap-5 md:flex-row">
            <ContactCard
              icon={calendar}
              title="Nežinote nuo ko pradėti?"
              description="Registruokitės nemokamai 15 min. konsultacijai ir kartu rasime geriausią sprendimą."
              buttonLabel="Registruotis konsultacijai"
              onClick={scrollToForm}
            />
            <ContactCard
              icon={phone}
              title="Mūsų kontaktai"
              description={
                <>
                  Ieškote atsakymo ar prireikė pagalbos?{" "}
                  <span
                    className="hover:text-primary cursor-pointer border-b-1 transition-colors"
                    onClick={() => setIsContactModalOpen(true)}
                  >
                    Parašykite mums žinutę
                  </span>{" "}
                  arba skambinkite
                </>
              }
              buttonLabel="Skambinti"
              onClick={() => {
                window.location.href = "tel:+37067909369";
              }}
            />
            <ContactCard
              icon={connection}
              title="Bendradarbiaukite su Telomera"
              description="Dirbame su įmonėmis, kurios darbuotojams nori suteikti pažangesnius prevencinės sveikatos sprendimus."
              buttonLabel="Bendradarbiauti"
              onClick={scrollToForm}
            />
          </div>
        </Container>
      </section>

      <section className="px-container md:pb-section w-full pb-5">
        <Container>
          <div className="md:gap-block rounded-card flex w-full flex-col border border-[#E7E7E7] md:min-h-[450px] md:flex-row md:items-stretch md:py-5 md:pr-5 md:pl-0">
            <div className="flex flex-1 flex-col justify-between p-5 md:p-[30px]">
              <div className="flex flex-col gap-[30px]">
                <div className="flex flex-col gap-5">
                  <p className="text-dark text-2xl leading-tight font-medium">
                    Mūsų adresas ir darbo laikas
                  </p>
                  <p>
                    Telomera.lt UAB <br />
                    Įm. kodas: 305022637 <br />
                    El. paštas: info@telomera.lt <br />
                    Tel. numeris: +370 (6) 79 09 369
                  </p>
                  <div className="text-dark text-base leading-relaxed">
                    <p className="m-0 mb-0 p-0 whitespace-pre">
                      I-IV 07:00 – 20:00
                    </p>
                    <p className="m-0 mb-0 p-0 whitespace-pre">
                      V 07:00 – 16:00
                    </p>
                    <p className="m-0 p-0 whitespace-pre">VI-VII Nedirbame</p>
                  </div>
                  <div className="text-dark text-base leading-relaxed">
                    <p className="m-0 mb-0 p-0">
                      Vilnius: Santaros g. 5 (Klinika)
                    </p>
                    <p className="m-0 mb-0 p-0">
                      Vilnius: Švitrigailos g. 11B (Procedūrinis)
                    </p>
                    <p className="m-0 mb-0 p-0">
                      Kaunas: Kęstučio g. 38 (Procedūrinis)
                    </p>
                    <p className="m-0 mb-0 p-0">
                      Panevėžys: P. Puzino g. 10 (Procedūrinis)
                    </p>
                    <p className="m-0 mb-0 p-0">
                      Šiauliai: Gumbinės g. 33C (Procedūrinis)
                    </p>
                    <p className="m-0 mb-0 p-0">
                      Mažeikiai: Naftininkų g. 9 (Procedūrinis)
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-6 md:mt-0">
                <div
                  className="hover:bg-surface-hover flex w-fit cursor-pointer items-center justify-center rounded-[38px] bg-[#E7E7E7] px-6 py-3 transition-colors md:mt-10"
                  onClick={() => {
                    const address =
                      "TELOMERA, Santaros g. 5, Vilnius, 08411 Vilniaus m. sav.";
                    const url = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
                    window.open(url, "_blank");
                  }}
                >
                  <span className="text-ink text-lg font-medium">
                    Atidaryti navigaciją
                  </span>
                </div>
              </div>
            </div>

            <div className="rounded-card relative h-[350px] w-full shrink-0 overflow-hidden md:h-auto md:w-[733px]">
              <div className="absolute inset-0">
                <Map
                  className="h-full w-full"
                  defaultCenter={{
                    lat: 54.757047024482546,
                    lng: 25.277205146843215,
                  }}
                  defaultZoom={15}
                  gestureHandling="greedy"
                  disableDefaultUI={true}
                  mapId="TELOMERA_MAP"
                >
                  <AdvancedMarker
                    position={{
                      lat: 54.757047024482546,
                      lng: 25.277205146843215,
                    }}
                  />
                </Map>
              </div>
            </div>
          </div>
        </Container>
      </section>

      <ContactModal
        isOpen={isContactModalOpen}
        onOpenChange={setIsContactModalOpen}
      />
    </div>
  );
};

export default Contacts;
